import ReactMarkdown, { type Components } from 'react-markdown';
import { cn } from '@/lib/utils';

interface MarkdownMessageProps {
  children: string;
  className?: string;
  components?: Components;
  onLinkClick?: (href: string) => void;
}

/** Shared presentation for AI replies. Keep rich-text editor HTML on its own path. */
const MarkdownMessage = ({ children, className, components, onLinkClick }: MarkdownMessageProps) => (
  <div
    className={cn(`min-w-0 max-w-full text-sm leading-relaxed [overflow-wrap:anywhere]
      [&_p]:my-2 [&_p]:whitespace-pre-line
      [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5
      [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5
      [&_li]:my-1 [&_li]:whitespace-pre-line [&_li>p]:my-1
      [&_strong]:font-semibold [&_em]:italic
      [&_h1]:my-2 [&_h1]:font-semibold [&_h2]:my-2 [&_h2]:font-semibold
      [&_h3]:my-2 [&_h3]:font-semibold [&_h4]:my-2 [&_h4]:font-semibold
      [&_h5]:my-2 [&_h5]:font-semibold [&_h6]:my-2 [&_h6]:font-semibold
      [&_blockquote]:my-2 [&_blockquote]:border-l-2 [&_blockquote]:border-current [&_blockquote]:pl-3
      [&_code]:rounded [&_code]:bg-black/10 [&_code]:px-1 [&_code]:font-mono [&_code]:text-xs
      [&_pre]:my-2 [&_pre]:overflow-x-auto [&_pre]:rounded [&_pre]:bg-black/10 [&_pre]:p-2
      [&_pre_code]:bg-transparent [&_pre_code]:p-0
      [&>:first-child]:mt-0 [&>:last-child]:mb-0`, className)}
  >
    <ReactMarkdown
      skipHtml
      disallowedElements={['img']}
      components={{
        a: ({ href, title, children }) => (
          <a
            href={href}
            title={title}
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2"
            onClick={(event) => {
              if (href && onLinkClick && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
                event.preventDefault();
                onLinkClick(href);
              }
            }}
          >
            {children}
          </a>
        ),
        ...components,
      }}
    >
      {children}
    </ReactMarkdown>
  </div>
);

export default MarkdownMessage;
